import type { TownFacing } from "@town/contracts";

/**
 * The landing page's walkable office: a hand-placed room, small enough to
 * cross in a few seconds, that shows the three things the real office does.
 * Coordinates are map tiles (32 texels each), x east and y south. Nothing here
 * touches the DOM, so the movement and zone rules run under test.
 */

export const LANDING_COLUMNS = 34;
export const LANDING_ROWS = 16;
/** The north wall's plaster face is two tiles tall; the floor starts below it. */
export const LANDING_WALL_ROWS = 2;
export const LANDING_SPAWN = { x: 17, y: 12 } as const;

export const PLAYER_HALF = 0.28;
export const WALK_SPEED = 4.2;
/** Close enough to hear each other on the open floor. */
export const TALK_RADIUS = 2.2;

export type Rect = Readonly<{ x0: number; y0: number; x1: number; y1: number }>;
export type Point = Readonly<{ x: number; y: number }>;

export type LandingZone = "floor" | "meeting" | "door";

/** The glass meeting room in the north-west corner. Its doorway is a gap in the south glass. */
export const MEETING_ROOM: Rect = { x0: 0.5, y0: LANDING_WALL_ROWS, x1: 10, y1: 8.5 };
export const MEETING_DOORWAY = { x0: 4.5, x1: 6.5 } as const;
/** The front door, set into the east wall. Walking into it opens the sign-in page. */
export const FRONT_DOOR: Rect = { x0: 33, y0: 10.5, x1: 34, y1: 13 };
const DOOR_TRIGGER_X = 32.6;

export type Furniture = Readonly<{
  id: string;
  rotation: 0 | 90 | 180 | 270;
  /** Footprint centre on the floor; the atlas anchors each crop there. */
  x: number;
  y: number;
  /** Footprint size in tiles for collision. Rugs and chairs leave it out. */
  solid?: Readonly<{ width: number; depth: number }>;
  /** Draw-order nudge for props that sit on or behind something at the same spot. */
  layer?: number;
  /** Lit at night, and in the meeting room once someone is inside. */
  light?: "lamp" | "screen";
}>;

export type Coworker = Readonly<{
  name: string;
  avatar: number;
  x: number;
  y: number;
  facing: TownFacing;
  seated: boolean;
  lines: readonly string[];
  /** Shared by coworkers in the same closed room: they hear each other and nobody outside. */
  room?: "meeting";
  /** A walking coworker loops these points, pausing at each. */
  route?: readonly Point[];
}>;

function desk(x: number, y: number, facing: "north" | "south"): Furniture[] {
  // A south-facing sitter works behind the desk, so the screen faces away from the camera.
  const monitorRotation = facing === "south" ? 180 : 0;
  const chairY = facing === "south" ? y - 0.72 : y + 0.85;
  return [
    { id: "official-desk-oak", rotation: 0, x, y, solid: { width: 2, depth: 1 } },
    { id: "official-monitor-set-black", rotation: monitorRotation, x: x - 0.35, y: y + (facing === "south" ? -0.15 : 0.1), layer: 1, light: "screen" },
    { id: facing === "south" ? "official-mug-coral" : "official-mug-teal", rotation: 0, x: x + 0.6, y: y + 0.1, layer: 1 },
    // Chairs always draw under their sitter, so a back-facing sitter stays visible over the backrest.
    { id: "official-chair-office-navy", rotation: facing === "south" ? 0 : 180, x: x - 0.3, y: chairY, layer: -1 },
  ];
}

export const LANDING_FURNITURE: readonly Furniture[] = [
  // Meeting room.
  { id: "official-rug-round-blue", rotation: 0, x: 5.5, y: 5.2, layer: -10 },
  { id: "official-table-round-2", rotation: 0, x: 5.5, y: 5.2, solid: { width: 1.8, depth: 1.5 } },
  { id: "official-chair-dining-orange", rotation: 0, x: 5.5, y: 3.9, layer: -1 },
  { id: "official-chair-dining-orange", rotation: 180, x: 5.5, y: 6.5, layer: -1 },
  { id: "official-laptop-silver", rotation: 0, x: 5.2, y: 5.1, layer: 1, light: "screen" },
  { id: "official-whiteboard-easel", rotation: 0, x: 8.6, y: 2.7, solid: { width: 1.4, depth: 0.5 } },
  { id: "official-plant-ficus", rotation: 0, x: 1.4, y: 2.8, solid: { width: 0.7, depth: 0.6 } },
  { id: "official-floor-lamp", rotation: 0, x: 9.3, y: 7.7, solid: { width: 0.5, depth: 0.5 }, light: "lamp" },

  // Garden nook under the meeting room.
  { id: "official-tree-cherry", rotation: 0, x: 2.2, y: 12.4, solid: { width: 1, depth: 0.8 } },
  { id: "official-sofa-2-green", rotation: 0, x: 6, y: 13.9, solid: { width: 2, depth: 0.9 } },
  { id: "official-planter-flowers-pink", rotation: 0, x: 8.8, y: 10.2, solid: { width: 1, depth: 0.6 } },
  { id: "official-street-lamp", rotation: 0, x: 4.4, y: 10, solid: { width: 0.4, depth: 0.4 }, light: "lamp" },

  // Open floor: two desk pods.
  ...desk(13.5, 5, "south"),
  ...desk(16.5, 5, "south"),
  ...desk(20.5, 5, "south"),
  ...desk(13.5, 9.6, "north"),
  ...desk(20.5, 9.6, "north"),
  { id: "official-plant-leafy-white", rotation: 0, x: 18.5, y: 5, solid: { width: 0.7, depth: 0.6 } },
  { id: "official-plant-leafy-terracotta", rotation: 0, x: 17, y: 9.6, solid: { width: 0.7, depth: 0.6 } },
  { id: "official-bookshelf-tall", rotation: 0, x: 11.4, y: 2.4, solid: { width: 1, depth: 0.5 } },
  { id: "official-bookshelf-tall", rotation: 0, x: 22.6, y: 2.4, solid: { width: 1, depth: 0.5 } },

  // Lounge.
  { id: "official-rug-rect-gold", rotation: 0, x: 28.4, y: 5, layer: -10 },
  { id: "official-sofa-3-green", rotation: 0, x: 28.4, y: 3.1, solid: { width: 3, depth: 0.9 } },
  { id: "official-table-coffee-round", rotation: 0, x: 28.4, y: 5.2, solid: { width: 0.9, depth: 0.8 } },
  { id: "official-armchair-orange", rotation: 180, x: 28.4, y: 6.7, layer: 1, solid: { width: 0.9, depth: 0.8 } },
  { id: "official-counter-kitchen", rotation: 0, x: 25.2, y: 2.6, solid: { width: 2, depth: 1 } },
  { id: "official-espresso-machine", rotation: 0, x: 24.8, y: 2.6, layer: 1 },
  { id: "official-water-cooler", rotation: 0, x: 26.7, y: 2.5, solid: { width: 0.5, depth: 0.5 } },
  { id: "official-arcade-machine", rotation: 0, x: 32.2, y: 2.6, solid: { width: 0.9, depth: 0.8 } },
  { id: "official-arcade-machine", rotation: 0, x: 31.2, y: 2.6, solid: { width: 0.9, depth: 0.8 } },
  { id: "official-floor-lamp", rotation: 0, x: 25.2, y: 5.8, solid: { width: 0.5, depth: 0.5 }, light: "lamp" },

  // Entrance hall.
  { id: "official-plant-palm", rotation: 0, x: 32.4, y: 9.4, solid: { width: 0.7, depth: 0.6 } },
  { id: "official-plant-palm", rotation: 0, x: 32.4, y: 14.4, solid: { width: 0.7, depth: 0.6 } },
  { id: "official-planter-trough", rotation: 0, x: 26.5, y: 14.8, solid: { width: 2, depth: 0.5 } },
  { id: "official-plant-round-terracotta", rotation: 0, x: 11, y: 14.6, solid: { width: 0.7, depth: 0.6 } },
];

export const LANDING_COWORKERS: readonly Coworker[] = [
  { name: "Thảo", avatar: 2, x: 13.2, y: 4.35, facing: "south", seated: true, lines: ["Morning! Coffee after standup?", "Pull up a chair. The demo is at three."] },
  { name: "Minh", avatar: 1, x: 20.2, y: 4.35, facing: "south", seated: true, lines: ["Want to pair on this bug?", "Ship it, then lunch."] },
  { name: "Khoa", avatar: 3, x: 13.2, y: 10.4, facing: "north", seated: true, lines: ["Headphones on means focus mode.", "Ask me anything. I'm right here."] },
  { name: "Linh", avatar: 4, x: 5.5, y: 4.35, facing: "south", seated: true, room: "meeting", lines: ["So, the plan for Q4…", "Can everyone see my screen?"] },
  { name: "Bảo", avatar: 5, x: 5.5, y: 6.75, facing: "north", seated: true, room: "meeting", lines: ["Looks good. Ship it Friday?", "Let's write that on the board."] },
  { name: "An", avatar: 6, x: 31.7, y: 3.6, facing: "north", seated: false, lines: ["Best of three on the arcade?", "Twelve wins in a row. Try me."] },
  {
    name: "Vy", avatar: 7, x: 18.5, y: 7.3, facing: "east", seated: false,
    lines: ["Walk with me, I'm getting snacks.", "Ping me if you get lost."],
    route: [{ x: 18.5, y: 7.3 }, { x: 24.2, y: 7.3 }, { x: 24.2, y: 4.2 }, { x: 24.2, y: 7.3 }, { x: 11.6, y: 7.3 }, { x: 11.6, y: 12.6 }, { x: 18.5, y: 12.6 }],
  },
];

/** Walls the player cannot cross. Glass walls leave the meeting room doorway open. */
export const LANDING_WALLS: readonly Rect[] = [
  { x0: 0, y0: 0, x1: LANDING_COLUMNS, y1: LANDING_WALL_ROWS },
  { x0: 0, y0: 0, x1: 0.5, y1: LANDING_ROWS },
  { x0: LANDING_COLUMNS - 1, y0: 0, x1: LANDING_COLUMNS, y1: FRONT_DOOR.y0 },
  { x0: LANDING_COLUMNS - 1, y0: FRONT_DOOR.y1, x1: LANDING_COLUMNS, y1: LANDING_ROWS },
  { x0: 0, y0: LANDING_ROWS - 0.5, x1: LANDING_COLUMNS, y1: LANDING_ROWS },
  // Meeting room glass: east wall, and the south wall either side of the doorway.
  { x0: MEETING_ROOM.x1 - 0.12, y0: MEETING_ROOM.y0, x1: MEETING_ROOM.x1 + 0.12, y1: MEETING_ROOM.y1 + 0.12 },
  { x0: 0, y0: MEETING_ROOM.y1 - 0.12, x1: MEETING_DOORWAY.x0, y1: MEETING_ROOM.y1 + 0.12 },
  { x0: MEETING_DOORWAY.x1, y0: MEETING_ROOM.y1 - 0.12, x1: MEETING_ROOM.x1 + 0.12, y1: MEETING_ROOM.y1 + 0.12 },
];

export const LANDING_SOLIDS: readonly Rect[] = [
  ...LANDING_WALLS,
  ...LANDING_FURNITURE.flatMap(({ x, y, solid }) => solid
    ? [{ x0: x - solid.width / 2, y0: y - solid.depth / 2, x1: x + solid.width / 2, y1: y + solid.depth / 2 }]
    : []),
  // Seated coworkers block the chair they sit on.
  ...LANDING_COWORKERS.filter((coworker) => coworker.seated)
    .map(({ x, y }) => ({ x0: x - 0.3, y0: y - 0.3, x1: x + 0.3, y1: y + 0.3 })),
];

function overlaps(point: Point, half: number, rect: Rect): boolean {
  return point.x + half > rect.x0 && point.x - half < rect.x1 && point.y + half > rect.y0 && point.y - half < rect.y1;
}

export function blocked(point: Point, solids: readonly Rect[] = LANDING_SOLIDS, half = PLAYER_HALF): boolean {
  return solids.some((rect) => overlaps(point, half, rect));
}

/**
 * One movement step that slides along whatever it hits: each axis moves on its
 * own, so walking diagonally into a desk keeps the free component.
 */
export function stepPlayer(from: Point, direction: Point, seconds: number, solids: readonly Rect[] = LANDING_SOLIDS): Point {
  const length = Math.hypot(direction.x, direction.y);
  if (!length || seconds <= 0) return from;
  // Small sub-steps keep a long frame from tunnelling through a thin glass wall.
  const steps = Math.max(1, Math.ceil((WALK_SPEED * seconds) / 0.1));
  const dx = (direction.x / length) * WALK_SPEED * seconds / steps;
  const dy = (direction.y / length) * WALK_SPEED * seconds / steps;
  let point = from;
  for (let step = 0; step < steps; step += 1) {
    const sideways = { x: point.x + dx, y: point.y };
    if (!blocked(sideways, solids)) point = sideways;
    const forward = { x: point.x, y: point.y + dy };
    if (!blocked(forward, solids)) point = forward;
  }
  return point;
}

export function facingFor(direction: Point, previous: TownFacing): TownFacing {
  if (!direction.x && !direction.y) return previous;
  if (Math.abs(direction.x) > Math.abs(direction.y)) return direction.x > 0 ? "east" : "west";
  return direction.y > 0 ? "south" : "north";
}

export function zoneAt(point: Point): LandingZone {
  if (point.x >= DOOR_TRIGGER_X && point.y > FRONT_DOOR.y0 && point.y < FRONT_DOOR.y1) return "door";
  if (point.x > MEETING_ROOM.x0 && point.x < MEETING_ROOM.x1 && point.y > MEETING_ROOM.y0 && point.y < MEETING_ROOM.y1) return "meeting";
  return "floor";
}

/**
 * Who the player can hear. Inside the meeting room it is everyone in the room
 * and nobody outside; on the open floor it is whoever stands within reach, but
 * never someone behind the meeting room glass.
 */
export function canHear(player: Point, coworker: Point & { room?: "meeting" }): boolean {
  const inRoom = zoneAt(player) === "meeting";
  if (coworker.room === "meeting") return inRoom;
  if (inRoom) return false;
  return Math.hypot(player.x - coworker.x, player.y - coworker.y) <= TALK_RADIUS;
}

/** Position along a looping route, with a pause at each point. */
export function routePosition(route: readonly Point[], seconds: number, pause = 1.6, speed = 1.8): { point: Point; direction: Point } {
  const legs = route.map((from, index) => {
    const to = route[(index + 1) % route.length]!;
    return { from, to, length: Math.hypot(to.x - from.x, to.y - from.y) };
  });
  const total = legs.reduce((sum, leg) => sum + pause + leg.length / speed, 0);
  let time = ((seconds % total) + total) % total;
  for (const leg of legs) {
    if (time < pause) return { point: leg.from, direction: { x: 0, y: 0 } };
    time -= pause;
    const duration = leg.length / speed;
    if (time < duration) {
      const t = time / duration;
      return {
        point: { x: leg.from.x + (leg.to.x - leg.from.x) * t, y: leg.from.y + (leg.to.y - leg.from.y) * t },
        direction: { x: leg.to.x - leg.from.x, y: leg.to.y - leg.from.y },
      };
    }
    time -= duration;
  }
  return { point: route[0]!, direction: { x: 0, y: 0 } };
}

const CELL = 0.25;
const GRID_COLUMNS = LANDING_COLUMNS / CELL;
const GRID_ROWS = LANDING_ROWS / CELL;

let walkable: Uint8Array | null = null;
function walkableGrid(): Uint8Array {
  if (walkable) return walkable;
  walkable = new Uint8Array(GRID_COLUMNS * GRID_ROWS);
  for (let row = 0; row < GRID_ROWS; row += 1) {
    for (let column = 0; column < GRID_COLUMNS; column += 1) {
      walkable[row * GRID_COLUMNS + column] = blocked(cellCentre(row * GRID_COLUMNS + column)) ? 0 : 1;
    }
  }
  return walkable;
}

function cellCentre(cell: number): Point {
  return { x: ((cell % GRID_COLUMNS) + 0.5) * CELL, y: (Math.floor(cell / GRID_COLUMNS) + 0.5) * CELL };
}

function cellAt(point: Point): number {
  const column = Math.min(GRID_COLUMNS - 1, Math.max(0, Math.floor(point.x / CELL)));
  const row = Math.min(GRID_ROWS - 1, Math.max(0, Math.floor(point.y / CELL)));
  return row * GRID_COLUMNS + column;
}

/** The walkable cell nearest a tapped point, so tapping a desk walks up to it. */
function nearestWalkable(cell: number): number | null {
  const grid = walkableGrid();
  if (grid[cell]) return cell;
  const seen = new Set([cell]);
  let frontier = [cell];
  while (frontier.length) {
    const next: number[] = [];
    for (const current of frontier) {
      for (const neighbour of neighbours(current)) {
        if (seen.has(neighbour)) continue;
        if (grid[neighbour]) return neighbour;
        seen.add(neighbour);
        next.push(neighbour);
      }
    }
    frontier = next;
  }
  return null;
}

function neighbours(cell: number): number[] {
  const column = cell % GRID_COLUMNS;
  const row = Math.floor(cell / GRID_COLUMNS);
  const result: number[] = [];
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    const x = column + dx;
    const y = row + dy;
    if (x >= 0 && y >= 0 && x < GRID_COLUMNS && y < GRID_ROWS) result.push(y * GRID_COLUMNS + x);
  }
  return result;
}

function clearLine(from: Point, to: Point): boolean {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  const samples = Math.ceil(length / 0.1);
  for (let sample = 1; sample <= samples; sample += 1) {
    const t = sample / samples;
    if (blocked({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t })) return false;
  }
  return true;
}

/**
 * Waypoints from a point to a tapped spot, routed around desks and through the
 * meeting room doorway. A breadth-first search over quarter-tile cells, then
 * each corner is dropped wherever a straight walk clears it.
 */
export function findPath(from: Point, to: Point): Point[] {
  const grid = walkableGrid();
  const start = nearestWalkable(cellAt(from));
  const goal = nearestWalkable(cellAt(to));
  if (start === null || goal === null) return [];
  const target = goal === cellAt(to) && !blocked(to) ? to : cellCentre(goal);
  const previous = new Map<number, number>([[start, start]]);
  let frontier = [start];
  while (frontier.length && !previous.has(goal)) {
    const next: number[] = [];
    for (const current of frontier) {
      for (const neighbour of neighbours(current)) {
        if (previous.has(neighbour) || !grid[neighbour]) continue;
        previous.set(neighbour, current);
        next.push(neighbour);
      }
    }
    frontier = next;
  }
  if (!previous.has(goal)) return [];
  const cells: Point[] = [];
  for (let cell = goal; cell !== start; cell = previous.get(cell)!) cells.push(cellCentre(cell));
  cells.reverse();
  cells[cells.length - 1] = target;
  const path: Point[] = [];
  let cursor = from;
  let index = 0;
  while (index < cells.length) {
    let farthest = index;
    for (let candidate = cells.length - 1; candidate > index; candidate -= 1) {
      if (clearLine(cursor, cells[candidate]!)) { farthest = candidate; break; }
    }
    cursor = cells[farthest]!;
    path.push(cursor);
    index = farthest + 1;
  }
  return path;
}
